'use strict'
/**
 * COLA:objetivos-que-la-base-no-admite · EL CATÁLOGO DE OBJETIVOS, con la misma fuente que el CHECK.
 *
 * Orden del founder (2026-09-24, P0): «`SELECTABLE_GOALS` sale del catálogo de objetivos (la misma fuente que el CHECK), guard select = contrato».
 *
 * QUÉ HABÍA. El selector de meta de Perfil tenía su lista **escrita a mano**:
 *   `['recompose','lose_fat','build_muscle','marathon','longevity','general','improve_endurance','prime_protocol','prime_essential']`
 * y **`general` no existe** para la base: el CHECK de `profiles.fitness_goal` admite `general_fitness`, no `general`. Una lista a mano al lado de un
 * CHECK es una copia que se desincroniza, y aquí ya lo estaba: la pantalla ofrecía un valor que la columna protegida rechaza.
 *
 * DE DÓNDE SALE AHORA. De `contracts/esquema.json` → `checks.profiles_fitness_goal_check.valores`, que es **el mismo CHECK de producción** (el
 * contrato se genera contra la base y su guard lo comprueba). No es una copia: es la misma fuente, leída.
 *
 * QUÉ AÑADE EL CATÁLOGO. El CHECK dice qué se puede **guardar**; no dice qué se puede **elegir**. Sus 26 valores mezclan dos vocabularios: las metas
 * del motor (`recompose`, `marathon`…) y las familias del alta (`body_composition`, `event_date`, `habit_change`…), que se eligen en el alta con su
 * subtipo y no como meta suelta. Aquí se declara, valor por valor, cuál de las dos cosas es — y el guard exige que **todos** tengan decisión: un valor
 * nuevo en el CHECK sin declarar pone el guard en rojo, que es cuando cuesta diez segundos decidirlo.
 */
// COLA:objetivos-que-la-base-no-admite · el dato entra en `src/` GENERADO desde `contracts/esquema.json` (scripts/qa/objetivosDelCheck.js): este
// módulo lo usa la pantalla, y el build de la interfaz no deja importar nada de fuera de `src/` — los tests pasaban y el build fallaba, lo cazó la
// puerta. El guard recalcula el generado contra el contrato y los compara: un generado que no se regenera vuelve a ser una copia.
const DEL_CHECK = require('../../data/objetivosDelCheck.json')
const { goalTitle } = require('../hub/labels')

/** Los valores que la columna admite, leídos del contrato del esquema (el mismo CHECK que corre en producción). */
const VALORES_DEL_CHECK = Object.freeze([...(DEL_CHECK.valores || [])])

/**
 * Qué es cada valor del CHECK:
 *   `meta`      · una meta del motor, elegible en Perfil como tal;
 *   `familia`   · una familia del alta (se elige allí, con su subtipo, no como meta suelta en Perfil);
 *   `heredado`  · vocabulario de una versión anterior que sigue en filas reales: se guarda y se lee, no se ofrece.
 */
const CLASE = Object.freeze({
  general_fitness: 'meta',
  recompose: 'meta',
  lose_fat: 'meta',
  build_muscle: 'meta',
  gain_muscle: 'heredado',            // el mismo objetivo que `build_muscle`, con otro nombre: no se ofrecen los dos
  marathon: 'meta',
  half_marathon: 'meta',
  '10k': 'meta',
  improve_endurance: 'meta',
  longevity: 'meta',
  health_and_longevity: 'heredado',   // mismo objetivo que `longevity`
  rehab_return: 'heredado',
  return_to_sport: 'meta',
  recover_form: 'meta',
  prime_protocol: 'meta',
  prime_essential: 'meta',
  performance_specific: 'familia',
  running: 'familia',
  start_running: 'meta',
  event_date: 'familia',
  body_composition: 'familia',
  start_training: 'meta',
  rehabilitation: 'familia',
  habit_change: 'familia',
  sport_performance: 'familia',
  other: 'heredado',                  // «otro» no es un objetivo: es lo que quedó sin clasificar
})

/**
 * COLA:el-objetivo-se-pierde-camino-del-progreso · LA FAMILIA DE PESOS DEL PRIME SCORE, valor por valor.
 *
 * 🔴 Había DOS mapas de objetivo escritos a mano, y los dos perdían gente por el camino:
 *   · `primeScoreOrchestrator.goalMap` conocía `marathon` pero **no `half_marathon`**, así que una
 *     corredora de media caía en `general` — los siete dominios a peso 1— por un `?? 'general'` mudo;
 *   · `profileUtils.GOAL_NORMALIZATION_MAP` traducía **`marathon` → `longevity`**: un maratoniano
 *     llegaba al Prime Score como objetivo de longevidad, con el sueño pesando más que el entreno.
 * Medido en producción (2026-09-25): de los **61 usuarios con Prime Score, 36** lo tienen calculado
 * sobre un objetivo distinto del de su perfil, y **31** han caído al cajón genérico. Los pesos son el
 * reparto de importancia entre los siete dominios: con el objetivo equivocado, el «dominio limitante»
 * —la palanca que la pantalla señala— es la de otra persona.
 *
 * Es la tercera vez que «media maratón» falta en un mapa (antes en `mapToEngineGoal` y en
 * `SELECTABLE_GOALS`). Por eso el mapa deja de escribirse a mano: vive aquí, con el catálogo, y el
 * guard exige que **todos** los valores del CHECK tengan decisión.
 *
 * `null` es una decisión, no un olvido: significa «aún no sé pesar este objetivo». El calculador usa
 * entonces los pesos neutros y **lo dice** (`pesos_familia: null`), en vez de disfrazarlo de `general`.
 */
const FAMILIA_DE_PESOS = Object.freeze({
  general_fitness: 'general',
  recompose: 'recompose',
  lose_fat: 'lose_fat',
  build_muscle: 'build_muscle',
  gain_muscle: 'build_muscle',
  // Preparar una carrera es preparar una carrera, sea de 10 km o de 42: mismos pesos (entreno 1,5 y
  // recuperación 1,4). Lo que cambia entre distancias es el plan, no el reparto de importancia.
  marathon: 'marathon',
  half_marathon: 'marathon',
  '10k': 'marathon',
  // Empezar a correr NO es preparar una carrera: la carga es otra, y los pesos, los de resistencia.
  start_running: 'improve_endurance',
  improve_endurance: 'improve_endurance',
  performance_specific: 'improve_endurance',
  longevity: 'longevity',
  health_and_longevity: 'longevity',
  prime_protocol: 'build_muscle',
  prime_essential: 'general',
  recover_form: 'recompose',
  // Volver de una lesión tiene su propio reparto, y no lo sabemos: se dice, no se disfraza de general.
  return_to_sport: null,
  rehab_return: null,
  rehabilitation: null,
  start_training: 'general',
  // Familias del alta: no llegan al Prime Score como meta suelta (se elige el subtipo), y si llegaran,
  // no sabríamos pesarlas.
  body_composition: null,
  event_date: null,
  habit_change: null,
  running: null,
  sport_performance: null,
  other: null,
})

/**
 * COLA:el-objetivo-se-quedo-en-el-archivo · EL VOCABULARIO DEL ARCHIVO v1, TRADUCIDO A LA COLUMNA CANÓNICA.
 *
 * 🔴 Censo de producción (2026-10-01, las 7 cuentas con Prime Score y sin `profiles.fitness_goal`):
 *   · **4 cuentas del 27-may** tienen su peso (78, 63, 91 y 67 kg) y su objetivo en `user_profiles` —el archivo v1— con el vocabulario de entonces:
 *     `performance`, `balanced`, `fat_loss`, `recomp`. Ninguno de esos cuatro nombres existe en el CHECK de `profiles.fitness_goal`.
 *   · **1 cuenta del 11-jun** tiene `goal: body_composition`, que **sí** es un valor del CHECK, y tampoco llegó a la columna canónica.
 *   · **2 cuentas del 7-ago** son altas de QA abandonadas a medias (`onboarding_completed: false`, sin fila en el archivo, 0 días): no son de aquí.
 * Ninguna es un alta real que perdiera la meta por el camino: el agujero es de la migración al modelo canónico, no del alta. Y mientras, las cinco
 * puntúan a diario con la familia **general** (100 de los 1742 días de la ventana de ±13 son suyos).
 *
 * Decisión del founder (2026-10-01): «`fat_loss` → `lose_fat`, `recomp` → `recompose`, `body_composition` y `balanced` → `general_fitness` pasan a la
 * canónica con el mapa de heredados en `catalogoDeObjetivos` y marca heredado; `performance` se pregunta por raíles, sin elegir por ellos».
 *
 * Traducir NO es elegir: la fila **ya declara** su objetivo y aquí sólo se le pone el nombre que la columna protegida admite. Donde el nombre viejo no
 * dice cuál de dos objetivos distintos era —`performance`: ¿el rendimiento en su deporte o la resistencia?— no se traduce: `null` significa **se
 * pregunta** (`preguntaDeObjetivoHeredado`), porque elegirlo por ellos sería inventarles el norte.
 */
const DEL_ARCHIVO_V1 = Object.freeze({
  fat_loss: 'lose_fat',          // el mismo objetivo con el nombre de entonces
  recomp: 'recompose',           // idem
  balanced: 'general_fitness',   // «equilibrado» es la forma general; decisión del founder (2026-10-01)
  body_composition: 'body_composition', // ya es un valor del CHECK (familia del alta): sólo le faltaba llegar a la canónica
  performance: null,             // SE PREGUNTA: «rendimiento en tu deporte» y «resistencia» son objetivos distintos, con pesos distintos
})

/** Los dos raíles de la pregunta de `performance`, decididos por el founder (2026-10-01). Los dos son valores del CHECK. */
const RAILES_DE_PERFORMANCE = Object.freeze([
  { value: 'sport_performance', label: 'Rendimiento en mi deporte' },
  { value: 'improve_endurance', label: 'Resistencia' },
])

/**
 * Qué hacer con un objetivo escrito en el archivo v1:
 *   · `{ canonico: 'lose_fat', traducible: true }`   → el mismo objetivo, con el nombre que la columna admite;
 *   · `{ canonico: null, traducible: false, pregunta: [...] }` → el nombre viejo no dice cuál era: se pregunta;
 *   · `{ canonico: null, traducible: false, pregunta: null }`  → ni del archivo ni del CHECK: no se toca nada.
 * Un valor que ya es del CHECK se devuelve tal cual (no se traduce lo que ya está bien).
 */
function delArchivoV1(objetivo) {
  const g = String(objetivo || '')
  if (!g) return { canonico: null, traducible: false, pregunta: null }
  if (Object.prototype.hasOwnProperty.call(DEL_ARCHIVO_V1, g)) {
    const c = DEL_ARCHIVO_V1[g]
    if (c && admiteLaBase(c)) return { canonico: c, traducible: true, pregunta: null }
    return { canonico: null, traducible: false, pregunta: g === 'performance' ? RAILES_DE_PERFORMANCE : null, heredado: g }
  }
  if (admiteLaBase(g)) return { canonico: g, traducible: true, pregunta: null }
  return { canonico: null, traducible: false, pregunta: null }
}

/**
 * ¿Hay que preguntarle su objetivo a este usuario, y con qué raíles? Se DERIVA de las dos filas, no se guarda una marca: una marca se queda vieja y
 * hay que mantenerla; el estado de la fila ya lo dice. Devuelve `null` si no hay nada que preguntar.
 * @param {object} perfil      fila de `profiles`
 * @param {object} archivo     fila de `user_profiles` (el archivo v1), si la hay
 */
function preguntaDeObjetivoHeredado(perfil = {}, archivo = {}) {
  if (perfil && perfil.fitness_goal) return null // ya tiene meta canónica: no se pregunta lo que está contestado
  const viejo = (perfil && perfil.goal) || (archivo && archivo.goal) || null
  if (!viejo) return null                        // nadie declaró nada: eso no se arregla preguntando por un nombre que no existe
  const r = delArchivoV1(viejo)
  if (r.traducible || !r.pregunta) return null   // traducible lo sana el sanador; sin raíles no hay pregunta que hacer
  return { heredado: viejo, opciones: r.pregunta }
}

/**
 * La familia de pesos de un objetivo: `{ familia, declarada }`.
 *   · `declarada: true` con familia → el reparto es el suyo;
 *   · `declarada: true` con familia `null` → sabemos que no sabemos: pesos neutros, y se dice;
 *   · `declarada: false` → el objetivo no está en el CHECK. Tampoco se disfraza.
 */
function familiaDePesos(objetivo) {
  const g = String(objetivo || '')
  if (!g) return { familia: null, declarada: false }
  if (!Object.prototype.hasOwnProperty.call(FAMILIA_DE_PESOS, g)) return { familia: null, declarada: false }
  return { familia: FAMILIA_DE_PESOS[g], declarada: true }
}

/** Los valores del CHECK sin familia DECLARADA (ni siquiera como `null`): el guard los pone en rojo. */
const sinFamiliaDeclarada = () => VALORES_DEL_CHECK.filter((g) => !Object.prototype.hasOwnProperty.call(FAMILIA_DE_PESOS, g))

/** Las metas que el usuario puede elegir en Perfil, en el orden en que se ofrecen. Todas están en el CHECK, por construcción. */
const SELECCIONABLES = Object.freeze(VALORES_DEL_CHECK.filter((g) => CLASE[g] === 'meta'))

/** `[{ value, label }]` para el selector. El copy sale de la fuente única (`hub/labels.goalTitle`), no de un mapa paralelo. */
const opcionesDelSelector = () => SELECCIONABLES.map((value) => ({ value, label: goalTitle(value) }))

const admiteLaBase = (g) => VALORES_DEL_CHECK.includes(String(g || ''))
const esSeleccionable = (g) => SELECCIONABLES.includes(String(g || ''))

/** Los valores del CHECK que nadie ha clasificado: si hay alguno, el catálogo está incompleto y el guard lo dice. */
const sinClasificar = () => VALORES_DEL_CHECK.filter((g) => !CLASE[g])

/**
 * ¿PIDE DETALLE ESTA FAMILIA? · COLA:el-alta-no-persiste-hasta-el-final
 * La lista de las quince (`quinceObligatorias.js`) dice que `goal_subtype` es «condicional, sólo si la familia lo pide». La condición es un hecho
 * de los objetivos, así que vive aquí, con ellos, y no repartida: la escribía el validador de `Block4Objetivo`, donde sólo esa pantalla podía
 * leerla, y por eso quien contaba las quince sobre la FILA la contaba siempre — «falta 1 de 15» a un alta entera y correcta, con el usuario parado
 * en la transición y su plan ya montado (medido en local el 2026-10-19 con `start_training`).
 *
 * 🔴 NO se deriva de `CLASE`, aunque lo parezca. `CLASE` dice si un valor llega al Prime Score como meta suelta o sólo como familia del alta, que es
 * otra pregunta: `rehabilitation` es `familia` ahí y aquí NO pide detalle. Derivarlo habría empezado a exigir un subtipo a quien vuelve de una
 * lesión — un cambio de producto colado por un refactor.
 */
const FAMILIAS_SIN_SUBTIPO = Object.freeze(['rehabilitation', 'start_training', 'longevity'])

/** ¿Está cubierto el detalle del objetivo? Vale igual para las respuestas del alta y para la fila: la clave se llama igual en las dos. */
function subtipoDelObjetivoCubierto({ primary_goal: objetivo, goal_subtype: detalle } = {}) {
  if (!objetivo) return true                                    // sin familia no hay detalle que pedir: lo cubre `primary_goal`
  if (FAMILIAS_SIN_SUBTIPO.includes(objetivo)) return true
  if (objetivo === 'habit_change') return Array.isArray(detalle) && detalle.length > 0
  return !!detalle
}

/**
 * EL OBJETIVO DECLARADO DEL PERFIL · orden del founder (2026-11-04): «`master_plans.goal` null es bug de escritor».
 *
 * La precedencia es la del motor, no una nueva: `resolveGoalFamily` (`goalOntology.js`) la compone igual para decidir la familia del arco, y de ahí
 * sale. Se declara aquí, con el catálogo, porque quien escribe una fila con el objetivo dentro necesita el MISMO valor que el motor usó para montar
 * el arco — si cada escritor lo compone, el rótulo del plan y su arco pueden decir cosas distintas.
 *
 * 🔴 DEVUELVE `null` CUANDO NO HAY NADA DECLARADO, y eso es un dato, no un fallo. Medido en producción el 2026-11-04 sobre los 74 planes activos:
 * 8 traían `goal`, 60 lo tenían a null con el objetivo vivo en el perfil (el lector de Progreso cae al perfil y sirve lo correcto), y **6 no
 * declaran objetivo en ninguna columna** — ni meta, ni intención, ni subtipo, ni fecha. A esos seis la pantalla les sirve el literal `general`, y
 * este lector no lo arregla: es hueco del alta, no del escritor. Inventarles una meta sería firmar un objetivo que nadie eligió.
 *
 * 🔴 Y POR QUÉ LA COLUMNA IMPORTA SI HAY FALLBACK. Porque el fallback lee el perfil de HOY y la fila es la memoria de lo que se montó. De los 8 que
 * traen `goal`, **3** tienen ya el objetivo del perfil a null: sin la columna, esas tres pantallas dirían `general` sobre un plan de longevidad.
 *
 * @param {object} perfil fila de `profiles`
 * @returns {string|null} el valor declarado, tal cual lo guarda la columna protegida por el CHECK, o `null` si no hay ninguno
 */
function objetivoDeclarado(perfil) {
  if (!perfil || typeof perfil !== 'object') return null
  const g = perfil.fitness_goal || perfil.primary_goal
  return g ? String(g) : null
}

module.exports = { VALORES_DEL_CHECK, objetivoDeclarado, FAMILIAS_SIN_SUBTIPO, subtipoDelObjetivoCubierto, CLASE, FAMILIA_DE_PESOS, DEL_ARCHIVO_V1, RAILES_DE_PERFORMANCE, delArchivoV1, preguntaDeObjetivoHeredado, familiaDePesos, sinFamiliaDeclarada, SELECCIONABLES, opcionesDelSelector, admiteLaBase, esSeleccionable, sinClasificar }
